import type { ReactElement } from "react";
import { useMemo } from "react";

import { ThemeProvider } from "styled-components";

import { FiltersPanel } from "app/pages/sales-intelligence/common-components/filters-panel/components/FiltersPanel/FiltersPanel";
import type { IFilterPanelContentProps } from "app/pages/sales-intelligence/common-components/filters-panel/types/props";
import type { IPlainObject } from "app/pages/sales-intelligence/types/plainObject";

import type { IFilterManagerModel } from "../../services/filters-manager/types";
import type { FilterModeType } from "../../types/misc";

import { createFiltersPanelTheme } from "./styles";

type Props<T extends IPlainObject> = Omit<
    IFilterPanelContentProps<T>,
    "filtersValue" | "filtersConfig" | "onFiltersUpdate"
> & {
    filterMode?: FilterModeType;
    withAiAgentFilterMode?: boolean;
    children: ReactElement | ReactElement[];
    filtersManager: IFilterManagerModel<T>;
};

export const FilterPanelContentContainer = <T extends IPlainObject = IPlainObject>({
    children,
    filtersManager,
    filterMode = "filters-list",
    withAiAgentFilterMode = false,
    onClearAllFilters,
    ...collapsiblePanelProps
}: Props<T>) => {
    const withBetaTag = withAiAgentFilterMode && filterMode === "ai-agent";

    const handleClearAllFilters = (): void => {
        filtersManager.resetAllFilters();

        onClearAllFilters?.();
    };

    const dirtyFilterKeys = filtersManager.getDirtyFilters();

    // Memoize so the ThemeProvider value identity is stable across renders — otherwise every styled
    // component in the panel re-renders on each parent render (theme subscription bypasses memo),
    // which is what made opening the category overlay deep-re-render the whole background panel.
    const theme = useMemo(() => createFiltersPanelTheme(filterMode), [filterMode]);

    return (
        <ThemeProvider theme={theme}>
            <FiltersPanel
                filtersCount={dirtyFilterKeys.length}
                withBetaTag={withBetaTag}
                onClear={handleClearAllFilters}
                {...collapsiblePanelProps}
            >
                {children}
            </FiltersPanel>
        </ThemeProvider>
    );
};
